import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Plus, Minus, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { CATEGORIES } from "@/lib/business";
import { useCart } from "@/context/CartContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Menu = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [opt, setOpt] = useState({});
  const [lb, setLb] = useState(null);
  const [loading, setLoading] = useState(true);
  const { items: cart, addItem, setQty } = useCart();

  const src = (img) => (img?.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`);
  const galleryOf = (item) => [...new Set([item.image, ...(item.images || [])].filter(Boolean))];
  const openLb = (item) => setLb({ imgs: galleryOf(item).map(src), i: 0, name: item.name });

  useEffect(() => {
    axios
      .get(`${API}/menu`)
      .then((r) => setItems(r.data))
      .catch((e) => {
        if (process.env.NODE_ENV === "development") console.error(e);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cats = CATEGORIES.filter((c) => items.some((i) => i.category === c.key));
  const current = active && cats.some((c) => c.key === active) ? active : cats[0]?.key;
  const filtered = items.filter((i) => i.category === current);
  const popular = items.filter((i) => i.popular);
  const imgSrc = (img) => (img?.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`);

  const quickAdd = (item) => {
    const hasOpts = Array.isArray(item.options) && item.options.length > 0;
    const sel = hasOpts ? item.options[0] : null;
    const price = hasOpts ? sel.price : item.price;
    const cartId = hasOpts ? `${item.id}::${sel.label}` : item.id;
    const cartName = hasOpts ? `${item.name} — ${sel.label}` : item.name;
    addItem({ id: cartId, name: cartName, price, image: item.image });
    toast.success(`${cartName} added to cart`);
  };

  return (
    <section id="menu" data-testid="menu-section" className="py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm tracking-[0.25em] uppercase text-brand font-bold">Our Menu</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-ink">
            Made fresh, served with pride
          </h2>
          <p className="mt-4 text-inkmuted">
            Every plate is prepared to order with fresh meat and authentic spices.
          </p>
        </div>

        {!loading && popular.length > 0 && (
          <div className="mt-12" data-testid="popular-row">
            <div className="flex items-center gap-2 mb-4">
              <Flame size={20} className="text-brand" />
              <h3 className="font-serif text-xl font-bold text-ink">Most Popular</h3>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x">
              {popular.map((item) => {
                const hasOpts = Array.isArray(item.options) && item.options.length > 0;
                const from = hasOpts ? Math.min(...item.options.map((o) => o.price)) : item.price;
                return (
                  <div key={item.id} data-testid={`popular-card-${item.id}`} className="snap-start shrink-0 w-56 bg-white rounded-2xl border border-line overflow-hidden">
                    <div className="h-32 w-full overflow-hidden">
                      <img src={imgSrc(item.image)} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <h4 className="font-serif font-semibold text-ink text-sm leading-tight">{item.name}</h4>
                      <p className="mt-1 text-brand font-bold text-sm">{hasOpts ? "from " : ""}${from.toFixed(2)}</p>
                      <button
                        onClick={() => quickAdd(item)}
                        data-testid={`popular-add-${item.id}`}
                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-brand text-white py-2 text-sm font-bold hover:bg-brand-hover transition-colors"
                      >
                        <Plus size={15} /> Quick Add
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="menu-categories">
          {cats.map((c) => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              data-testid={`menu-tab-${c.key}`}
              className={`rounded-full px-6 py-2.5 text-sm font-bold transition-all ${
                current === c.key
                  ? "bg-brand text-white shadow-md"
                  : "bg-white text-ink border border-line hover:border-brand/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center mt-16 text-inkmuted">Loading menu…</p>
        ) : (
          <motion.div
            key={current}
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.06 } } }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filtered.map((item) => (
              <motion.article
                key={item.id}
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                data-testid={`menu-item-${item.id}`}
                className="bg-white rounded-2xl overflow-hidden border border-line hover:border-brand/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden cursor-pointer group" onClick={() => openLb(item)} data-testid={`menu-image-${item.id}`}>
                  <img src={src(item.image)} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {item.popular && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gold text-ink text-xs font-bold px-3 py-1 rounded-full">
                      <Flame size={13} /> Popular
                    </span>
                  )}
                  {galleryOf(item).length > 1 && (
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-ink/70 text-cream text-xs font-bold px-2.5 py-1 rounded-full">
                      <Images size={13} /> {galleryOf(item).length}
                    </span>
                  )}
                </div>
                <div className="p-6">
                  {(() => {
                    const hasOpts = Array.isArray(item.options) && item.options.length > 0;
                    const selIdx = opt[item.id] ?? 0;
                    const sel = hasOpts ? item.options[selIdx] : null;
                    const price = hasOpts ? sel.price : item.price;
                    const cartId = hasOpts ? `${item.id}::${sel.label}` : item.id;
                    const cartName = hasOpts ? `${item.name} — ${sel.label}` : item.name;
                    const inCart = cart.find((c) => c.id === cartId);
                    return (
                      <>
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-serif text-xl font-semibold text-ink">{item.name}</h3>
                          <span className="font-serif text-lg font-bold text-brand whitespace-nowrap">
                            ${price.toFixed(2)}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-inkmuted leading-relaxed">{item.description}</p>
                        {hasOpts && (
                          <div className="mt-4 flex flex-wrap gap-2" data-testid={`menu-options-${item.id}`}>
                            {item.options.map((o, oi) => (
                              <button
                                key={o.label}
                                onClick={() => setOpt((p) => ({ ...p, [item.id]: oi }))}
                                data-testid={`menu-option-${item.id}-${oi}`}
                                className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${
                                  selIdx === oi ? "bg-brand text-white border-brand" : "bg-white text-ink border-line hover:border-brand/40"
                                }`}
                              >
                                {o.label} · ${o.price.toFixed(2)}
                              </button>
                            ))}
                          </div>
                        )}
                        {inCart ? (
                          <div className="mt-4 flex items-center justify-between">
                            <div className="inline-flex items-center gap-3 bg-brand/5 border border-brand/30 rounded-full px-2 py-1">
                              <button onClick={() => setQty(cartId, inCart.qty - 1)} data-testid={`menu-dec-${item.id}`} className="w-8 h-8 flex items-center justify-center text-brand hover:bg-brand/10 rounded-full" aria-label="Decrease">
                                <Minus size={16} />
                              </button>
                              <span className="w-6 text-center font-bold text-ink" data-testid={`menu-qty-${item.id}`}>{inCart.qty}</span>
                              <button onClick={() => setQty(cartId, inCart.qty + 1)} data-testid={`menu-inc-${item.id}`} className="w-8 h-8 flex items-center justify-center text-brand hover:bg-brand/10 rounded-full" aria-label="Increase">
                                <Plus size={16} />
                              </button>
                            </div>
                            <span className="text-sm font-semibold text-olive">In cart</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => { addItem({ id: cartId, name: cartName, price, image: item.image }); toast.success(`${cartName} added to cart`); }}
                            data-testid={`menu-add-${item.id}`}
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white py-3 font-bold hover:bg-brand-hover transition-colors"
                          >
                            <Plus size={18} /> Add to Cart
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </div>

      <Dialog open={Boolean(lb)} onOpenChange={(o) => !o && setLb(null)}>
        <DialogContent className="p-0 bg-transparent border-0 shadow-none max-w-3xl w-fit" data-testid="menu-lightbox">
          {lb && (
            <div className="relative flex flex-col items-center">
              <img src={lb.imgs[lb.i]} alt={lb.name} className="max-h-[80vh] w-auto rounded-2xl object-contain" data-testid="menu-lightbox-img" />
              {lb.imgs.length > 1 && (
                <>
                  <button onClick={() => setLb((s) => ({ ...s, i: (s.i - 1 + s.imgs.length) % s.imgs.length }))} data-testid="lightbox-prev" className="absolute left-2 top-1/2 -translate-y-1/2 bg-ink/70 text-cream rounded-full p-2 hover:bg-ink transition-colors" aria-label="Previous">
                    <ChevronLeft size={22} />
                  </button>
                  <button onClick={() => setLb((s) => ({ ...s, i: (s.i + 1) % s.imgs.length }))} data-testid="lightbox-next" className="absolute right-2 top-1/2 -translate-y-1/2 bg-ink/70 text-cream rounded-full p-2 hover:bg-ink transition-colors" aria-label="Next">
                    <ChevronRight size={22} />
                  </button>
                  <div className="mt-3 flex items-center gap-2">
                    {lb.imgs.map((_, di) => (
                      <button key={di} onClick={() => setLb((s) => ({ ...s, i: di }))} className={`w-2.5 h-2.5 rounded-full transition-colors ${di === lb.i ? "bg-white" : "bg-white/40"}`} aria-label={`Photo ${di + 1}`} />
                    ))}
                  </div>
                </>
              )}
              <p className="mt-2 text-cream text-sm font-semibold bg-ink/70 px-4 py-2 rounded-full">{lb.name}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
